// External
import { useCallback, useMemo } from "react";
import { useDispatch } from "Hooks";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { setToastMsg } from "Login/modules/LoginModule";
import { generateRandomId } from "Utils";

// Relative
import { GRADE_SCALE_TYPE_LOCALE_MAP } from "../../../modules/constants";
import {
  buildGradeScaleInput,
  canDeleteCustomColumn,
  CUSTOM_TAB_KEY,
  getInitialCustomColumns,
  getSchoolColumns,
  getSchoolTabs,
} from "../modules/configureColumnsModalUtils";
import { useCustomModalEntities } from "TranscriptsV3/hooks";

export const useConfigureColumnsModalData = ({
  onSave,
  academicRecordsData,
  curriculumProgramsData,
  transcriptGradeScalesData,
  selectedGradeId,
}) => {
  const { t } = useTranslation(["common", "transcript", "academicSetup"]);
  const dispatch = useDispatch();

  const getGradeScaleTypeLabel = useCallback(
    gradeScaleType => {
      const localeKey = _.get(GRADE_SCALE_TYPE_LOCALE_MAP, gradeScaleType);
      return localeKey ? t(localeKey) : "";
    },
    [t]
  );

  const schoolTabs = useMemo(
    () => getSchoolTabs({ academicRecordsData, selectedGradeId }),
    [academicRecordsData, selectedGradeId]
  );

  const curriculumProgramsById = useMemo(
    () =>
      _.reduce(
        curriculumProgramsData,
        (acc, program) => {
          const id = _.get(program, "id");
          if (!id) return acc;
          acc[id] = program;
          return acc;
        },
        {}
      ),
    [curriculumProgramsData]
  );

  const tabOptions = useMemo(
    () => [
      ..._.map(schoolTabs, school => ({
        value: school.id,
        label: school.label,
      })),
      {
        value: CUSTOM_TAB_KEY,
        label: t("transcript:custom_column_other"),
      },
    ],
    [schoolTabs, t]
  );

  const initialCustomColumns = useMemo(
    () =>
      getInitialCustomColumns({
        transcriptGradeScalesData,
        generateId: generateRandomId,
      }),
    [transcriptGradeScalesData]
  );

  const {
    entities: customColumns,
    setEntities: setCustomColumns,
    entitiesViewModel: customColumnsViewModel,
    validateEntities,
    selectedTab,
    handleTabChange: setSelectedTab,
    handleAddEntity: addCustomColumn,
    addEntityWithValue: addColumnWithName,
    handleEntityChange: handleCustomColumnNameChange,
    handleEntityDelete,
  } = useCustomModalEntities({
    initialEntities: initialCustomColumns,
    initialTab: _.get(tabOptions, "[0].value", CUSTOM_TAB_KEY),
    valueKey: "name",
  });

  const selectedSchoolName = useMemo(() => {
    const selectedSchool = _.find(
      schoolTabs,
      school => _.get(school, "id") === selectedTab
    );

    return _.get(selectedSchool, "label", "");
  }, [schoolTabs, selectedTab]);

  const schoolColumns = useMemo(
    () => getSchoolColumns({ curriculumProgramsById, selectedTab }),
    [curriculumProgramsById, selectedTab]
  );

  const schoolGridHeaders = useMemo(
    () => [
      {
        id: "name",
        colId: "name",
        title: t("common:grade_scales"),
        flex: 1,
      },
    ],
    [t]
  );

  const schoolGridData = useMemo(
    () =>
      _.map(schoolColumns, column => ({
        rowId: column.id,
        columns: [
          {
            value: column.name,
            subtitle: getGradeScaleTypeLabel(column.type),
            cellType: "label",
            isEditable: false,
          },
        ],
      })),
    [getGradeScaleTypeLabel, schoolColumns]
  );

  const deleteCustomColumn = useCallback(
    id => {
      const column = _.find(customColumns, { id });
      if (canDeleteCustomColumn(column)) {
        handleEntityDelete(id);
      }
    },
    [customColumns, handleEntityDelete]
  );

  const isCustomTab = selectedTab === CUSTOM_TAB_KEY;
  const hasTabs = !_.isEmpty(tabOptions);

  const handleSave = useCallback(() => {
    if (!onSave) return;

    if (isCustomTab && validateEntities()) {
      dispatch(
        setToastMsg({
          type: "error",
          msg: "transcript:fill_required_fields_error",
        })
      );
      return;
    }

    onSave(
      buildGradeScaleInput({
        customColumns,
        initialCustomColumns,
      })
    );
  }, [
    customColumns,
    dispatch,
    initialCustomColumns,
    isCustomTab,
    onSave,
    validateEntities,
  ]);

  return {
    addColumnWithName,
    addCustomColumn,
    customColumns,
    customColumnsViewModel,
    deleteCustomColumn,
    handleCustomColumnNameChange,
    handleSave,
    hasTabs,
    isCustomTab,
    setCustomColumns,
    schoolGridData,
    schoolGridHeaders,
    selectedSchoolName,
    selectedTab,
    setSelectedTab,
    t,
    tabOptions,
  };
};
